/**
 * Surface and Audits Chart Component
 * Displays historical surface (planned/audited) and number of audits by season
 */

import { Card } from 'antd';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { HistoricalProductionData } from '../../services/api/productionDashboardService';

interface SurfaceAuditsChartProps {
  data: HistoricalProductionData[];
}

export const SurfaceAuditsChart: React.FC<SurfaceAuditsChartProps> = ({ data }) => {
  // Format data for chart
  const chartData = data.map(item => ({
    name: item.season_name,
    'Superficie Planificada': parseFloat(item.planned_surface.toFixed(2)),
    'Superficie Auditada': parseFloat(item.audited_surface.toFixed(2)),
    'Nº Auditorías': item.completed_audits_count,
  }));

  // Custom tooltip formatter
  const formatSurfaceTooltip = (value: number) => {
    return new Intl.NumberFormat('es-ES', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value) + ' ha';
  };

  const formatAuditsTooltip = (value: number) => {
    return value.toString();
  };

  return (
    <Card title="Evolución de Superficie y Auditorías por Campaña">
      <ResponsiveContainer width="100%" height={400}>
        <ComposedChart
          data={chartData}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="name" />
          <YAxis
            yAxisId="left"
            tickFormatter={(value) =>
              new Intl.NumberFormat('es-ES', {
                notation: 'compact',
                compactDisplay: 'short',
              }).format(value)
            }
            label={{ value: 'Superficie (ha)', angle: -90, position: 'insideLeft' }}
          />
          <YAxis
            yAxisId="right"
            orientation="right"
            label={{ value: 'Nº Auditorías', angle: 90, position: 'insideRight' }}
          />
          <Tooltip
            formatter={(value: any, name: string) => {
              if (name === 'Nº Auditorías') {
                return formatAuditsTooltip(value);
              }
              return formatSurfaceTooltip(value);
            }}
          />
          <Legend />
          <Bar
            yAxisId="left"
            dataKey="Superficie Planificada"
            fill="#91d5ff"
            name="Superficie Planificada"
          />
          <Bar
            yAxisId="left"
            dataKey="Superficie Auditada"
            fill="#1890ff"
            name="Superficie Auditada"
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="Nº Auditorías"
            stroke="#52c41a"
            strokeWidth={3}
            name="Nº Auditorías"
            dot={{ r: 5 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Card>
  );
};
